import { ApiResponse } from "@shared/lib/interfaces/types";

export interface HistorySmallReportResponse extends ApiResponse<HistorySmallReportData> {}
export interface HistoryUserReportsResponse extends ApiResponse<UserReport[]> {}
export interface HistoryFullReportResponse extends ApiResponse<HistoryFullReportData> {}
export interface ReportProductsResponse extends ApiResponse<ReportProduct[]> {}
export interface UserReportBalanceResponse extends ApiResponse<UserReportBalance> {}
export interface CreateReportOrderResponse
    extends ApiResponse<ReportOrderData, { payment: { params: PaymentParams } }> {}
export interface CreateFullReportResponse extends ApiResponse<CreateFullReportData> {}
export interface ReportOrderStatusResponse extends ApiResponse<ReportOrderStatus> {}
export interface RecentReportByCodeResponse extends ApiResponse<RecentReportByCode> {}

export interface HistorySmallReportData {
    vin: string;
    regNumber: string;
    brand: string;
    model: string;
    year: number;
}

export interface UserReport {
    id: string;
    vin: string;
    regNumber: string;
    brand: string;
    model: string;
    year: number;
    status: number;
}

export interface HistoryFullReportData {
    id: string;
    status: ReportStatus;
    brand: string;
    model: string;
    generation: string;
    color: string;
    horsePower: number;
    category: string;
    maxWeight: number;
    minWeight: number;
    year: number;
    bodyNumber: string;
    engineNumber: string;
    crashes: boolean;
    ownersCount: number;
    actualRegNumber: string;
    vin: string;
    bodyType: string;
    driveType: string;
    wheel: string;
    engineType: string;
    transmission: string;
    displacement: number;
    doorsCount: number;
    hasFines: boolean;
    ptsType?: string | null;
    accidents: ReportAccidentsData;
    ownersHistory: ReportOwnerEntry[];
    techInspectionHistory?: {
        events?: TechInspectionEvent[];
    };
    pledge: ReportLegalItem;
    registration: ReportLegalItem;
    stealing: ReportLegalItem;
    taxiData: UsageData;
    carsharingData: UsageData;
    insurancePayments?: InsurancePayment[] | null;
    salvageCarAuctionRecords?: SalvageCarAuctionRecord[] | null;
    createdAt: string;
    historyMileage: ReportOtherEvent[] | null;
    eventsOthersHistory: ReportOtherEvent[];
    fines?: unknown;
    insurancePolicies: {
        events: RawInsurancePolicyEvent[];
        recaps: InsurancePolicyRecap[];
    };
    leasing: unknown | null;
    ptsData?: PtsData | null;
    raw?: any;
}

export interface ReportOwnerEntry {
    type: number;
    owner: string;
    title: string;
    period: string;
    region: string;
}

export interface ReportLegalItem {
    text: string;
    status: "ok" | "error" | string;
    requestTime: number;
}

export interface TechInspectionEvent {
    startDate: number;
    endDate: number;
    mileage: number;
    cardNumber: string;
}

export interface UsageData {
    events: unknown;
    recaps: UsageRecap[];
}

export interface UsageRecap {
    type: string;
    label: string;
    status: "ok" | "error" | string;
    description: string | null;
}

export interface ReportAccidentsData {
    requestTime: number;
    crashesHistory: ReportCrashEntry[];
}

export interface ReportCrashEntry {
    date: number;
    type: string;
    region: string;
    damageMap: string[];
    additional: unknown[];
    damageTypes: ReportDamageType[];
}

export interface ReportDamageType {
    slug: DamageSlug;
    type: DamageTypeColor;
    description: string;
}

export type DamageSlug =
    | "front-right"
    | "right-front-door"
    | "front-right-side"
    | "left-front"
    | "left-front-door"
    | "left-front-side"
    | "right-rear"
    | "right-rear-door"
    | "right-rear-side"
    | "left-rear"
    | "left-rear-door"
    | "left-rear-side"
    | "roof"
    | "bottom"
    | "front-hood"
    | "rear-hood";

export type DamageTypeColor = "yellow" | "red" | "other";

export interface ReportOtherEvent {
    date: number;
    type: string;
    event: string;
    source: string;
    mileage: number;
    description: string;
}

export interface RawOtherEvent {
    date?: number;
    type?: string;
    event?: string;
    source?: string;
    mileage?: number;
    description?: string;
}

export interface RawInsurancePolicyEvent {
    kmb?: number;
    status?: string;
    endDate?: number;
    isTransit?: boolean;
    startDate?: number;
    createDate?: number;
    hasTrailer?: boolean;
    companyName?: string;
    usageTarget?: string;
    policyIsRestrict?: boolean;
    restrictionCount?: number;
}

export interface InsurancePolicyRecap {
    type?: string;
    label?: string;
    status?: "ok" | "error" | string;
    description?: string | null;
}

export interface ReportProduct {
    id: string;
    name: string;
    description: string;
    price: number;
    newPrice: number | null;
}

export interface CreateReportOrderBody {
    productUuid: string;
}

export interface UserReportBalance {
    amount: number;
}

export interface ReportOrderDetail {
    id: string;
    productUuid: string;
    name: string;
    description: string;
    price: number;
    quantity: number;
}

export interface ReportOrderData {
    id: string;
    status: number;
    details: ReportOrderDetail[];
}

export interface PaymentParams {
    close_on_missclick: boolean;
    customer_id: string;
    payment_amount: number;
    payment_currency: string;
    payment_description: string;
    payment_id: string;
    project_id: number;
    signature: string;
    target_element: string;
}

export interface CreateFullReportData {
    id: string;
    status: ReportStatus;
}

export enum ReportStatus {
    Unknown = 0,
    Success = 1,
    Wait = 2,
    Processing = 3,
    NotFound = 4
}

export interface InsurancePayment {
    companyName: string | null;
    isCourtPayment?: boolean | null;
    isForVictim: boolean;
    month?: number | null;
    paymentAmount: number;
    year: number;
}

export interface SalvageCarAuctionRecord {
    auction: string;
    date: string;
    description?: string | null;
    images: string[];
    mileage?: number | null;
    region?: string | null;
}

export interface PtsData {
    pts?: {
        createdAt?: number | null;
        organization?: string | null;
        ptsNumber?: string;
        requestTime?: number;
    };
    sts?: {
        createdAt?: number | null;
        stsNumber?: string;
        requestTime?: number;
    };
}

export interface ReportOrderStatus {
    id: string;
    status: number;
    customerUuid: string;
}

export interface RecentReportByCode {
    id: string | null;
}